import { apiClient } from "../core/apiClient";
import { buildUrlWithParams } from "../core/utils/urlUtils";
import { fetchWithMockFallback } from "../core/utils/mockUtils";
import { SEARCH_ENDPOINTS } from "./endpoints";
import type { SearchResults } from "./interfaces/SearchResults";
import type { AdvancedContentSearchRequest } from "./interfaces/AdvancedContentSearchRequest";
import type { AdvancedContentSearchResponse } from "./interfaces/AdvancedContentSearchResponse";
import { getMockAdvancedContentSearch } from "./mockData";
import type {
  ContentType,
  Genre,
  Platform,
  Region,
  ConsumeType,
} from "../../constants/enums";

export interface SearchParams {
  category?: string;
  query: string;
  [key: string]: string | number | boolean | undefined;
}

export interface AdvancedSearchParams {
  name?: string;
  categories?: ContentType[];
  genres?: Genre[];
  dateFrom?: string;
  dateTo?: string;
  ratingPlatform?: Platform;
  ratingFrom?: string;
  ratingTo?: string;
  country?: Region;
  durationFrom?: string;
  durationTo?: string;
  participantId?: string;
  statuses?: ConsumeType[];
}

/**
 * Поиск контента по запросу
 * Опционально требует авторизации
 *
 * @param params - параметры поиска
 * @param requiresAuth - требуется ли авторизация
 * @returns результаты поиска
 */
export async function search(
  params: SearchParams,
  requiresAuth: boolean = false,
): Promise<SearchResults> {
  const url = buildUrlWithParams(SEARCH_ENDPOINTS.base, params);

  return apiClient<SearchResults>(url, {
    method: "GET",
    requiresAuth,
  });
}

/**
 * Расширенный поиск контента (новая реализация с GET запросом)
 * При ошибке возвращает mock данные
 *
 * @param params - параметры поиска контента
 * @param requiresAuth - требуется ли авторизация (опционально для получения user-specific данных)
 * @returns результаты поиска с пагинацией
 */
export async function advancedContentSearch(
  params: AdvancedContentSearchRequest,
  requiresAuth: boolean = false,
): Promise<AdvancedContentSearchResponse> {
  // Подготовка параметров для query string
  const queryParams: Record<string, string | number | string[]> = {};

  if (params.name) {
    queryParams.name = params.name;
  }

  if (params.categories && params.categories.length > 0) {
    queryParams.categories = params.categories.join(",");
  }

  if (params.genres && params.genres.length > 0) {
    queryParams.genres = params.genres.join(",");
  }

  if (params.dateFrom) {
    queryParams.dateFrom = params.dateFrom;
  }

  if (params.dateTo) {
    queryParams.dateTo = params.dateTo;
  }

  if (params.ratingPlatform !== undefined && params.ratingPlatform !== null) {
    queryParams.ratingPlatform = params.ratingPlatform;
  }

  if (params.ratingFrom !== undefined && params.ratingFrom !== null) {
    queryParams.ratingFrom = params.ratingFrom;
  }

  if (params.ratingTo !== undefined && params.ratingTo !== null) {
    queryParams.ratingTo = params.ratingTo;
  }

  if (params.country !== undefined && params.country !== null) {
    queryParams.country = params.country;
  }

  if (params.durationFrom !== undefined && params.durationFrom !== null) {
    queryParams.durationFrom = params.durationFrom;
  }

  if (params.durationTo !== undefined && params.durationTo !== null) {
    queryParams.durationTo = params.durationTo;
  }

  if (params.participantId) {
    queryParams.participantId = params.participantId;
  }

  if (params.statuses && params.statuses.length > 0) {
    queryParams.statuses = params.statuses.join(",");
  }

  if (params.page !== undefined && params.page !== null) {
    queryParams.page = params.page;
  }

  if (params.pageSize !== undefined && params.pageSize !== null) {
    queryParams.pageSize = params.pageSize;
  }

  const url = buildUrlWithParams(SEARCH_ENDPOINTS.advancedContent, queryParams);

  return fetchWithMockFallback(
    () =>
      apiClient<AdvancedContentSearchResponse>(url, {
        method: "GET",
        requiresAuth,
      }),
    () => getMockAdvancedContentSearch(params.page, params.pageSize),
    "advanced content search",
  );
}

/**
 * Расширенный поиск (старая реализация с GraphQL)
 * @deprecated Используйте advancedContentSearch вместо этого
 */
export async function advancedSearch(
  params: AdvancedSearchParams,
): Promise<SearchResults> {
  // Build GraphQL query based on provided params
  const query = buildAdvancedSearchQuery(params);

  return apiClient<SearchResults>("/search/advanced", {
    method: "POST",
    body: JSON.stringify({ query }),
    requiresAuth: false,
  });
}

function buildAdvancedSearchQuery(params: AdvancedSearchParams): string {
  const filters: string[] = [];

  if (params.name) {
    filters.push(`name: "${params.name}"`);
  }

  if (params.categories && params.categories.length > 0) {
    filters.push(`categories: [${params.categories.join(", ")}]`);
  }

  if (params.genres && params.genres.length > 0) {
    filters.push(`genres: [${params.genres.join(", ")}]`);
  }

  if (params.dateFrom) {
    filters.push(`dateFrom: "${params.dateFrom}"`);
  }

  if (params.dateTo) {
    filters.push(`dateTo: "${params.dateTo}"`);
  }

  if (params.ratingPlatform !== null && params.ratingPlatform !== undefined) {
    filters.push(`ratingPlatform: ${params.ratingPlatform}`);
  }

  if (params.ratingFrom) {
    filters.push(`ratingFrom: ${parseFloat(params.ratingFrom)}`);
  }

  if (params.ratingTo) {
    filters.push(`ratingTo: ${parseFloat(params.ratingTo)}`);
  }

  if (params.country !== null && params.country !== undefined) {
    filters.push(`country: ${params.country}`);
  }

  if (params.durationFrom) {
    filters.push(`durationFrom: ${parseInt(params.durationFrom, 10)}`);
  }

  if (params.durationTo) {
    filters.push(`durationTo: ${parseInt(params.durationTo, 10)}`);
  }

  if (params.participantId) {
    filters.push(`participantId: "${params.participantId}"`);
  }

  if (params.statuses && params.statuses.length > 0) {
    filters.push(`statuses: [${params.statuses.join(", ")}]`);
  }

  const filtersString = filters.length > 0 ? `(${filters.join(", ")})` : "";

  return `
    query {
      advancedSearch${filtersString} {
        movies {
          id
          title
          releaseYear
          consumeType
          image
        }
        serials {
          id
          title
          releaseYear
          consumeType
          image
        }
        books {
          id
          title
          releaseYear
          consumeType
          image
        }
        games {
          id
          title
          releaseYear
          consumeType
          image
        }
        persons {
          id
          name
          image
        }
        users {
          id
          username
          image
          reputation
        }
        collections {
          id
          name
          image
        }
      }
    }
  `.trim();
}
